import { motion } from "framer-motion";
import { Crown, Tag, Zap, AlertTriangle, Check } from "lucide-react";

const proofLines = [
  "Analyzed 27 relevant options",
  "Filtered weak options",
  "Compared total cost",
  "Evaluated delivery reality",
  "Ranked by real outcome",
];

const results = [
  {
    label: "Recommended Decision",
    icon: Crown,
    name: "Optimum Nutrition Gold Standard Whey",
    price: "$58.99",
    delivery: "Tomorrow by 10pm",
    why: "Strongest trust score, reliable next-day delivery, competitive price.",
    tradeoff: "Not the cheapest. Budget options save $20 but sacrifice taste.",
    badgeClass: "bg-primary/12 text-primary border-primary/15",
    gradient: "from-primary/8 to-card",
  },
  {
    label: "Best Value",
    icon: Tag,
    name: "Dymatize ISO100",
    price: "$42.99",
    delivery: "2 days",
    why: "Saves $16 with nearly identical macros and solid reviews.",
    tradeoff: "Slightly slower delivery. Fewer flavor options.",
    badgeClass: "bg-accent/12 text-accent border-accent/15",
    gradient: "from-accent/5 to-card",
  },
  {
    label: "Fastest Option",
    icon: Zap,
    name: "Ghost Whey Protein",
    price: "$64.99",
    delivery: "Same day",
    why: "Same-day delivery with strong brand loyalty.",
    tradeoff: "Higher total cost for similar nutrition.",
    badgeClass: "bg-premium-accent/12 text-premium-accent border-premium-accent/15",
    gradient: "from-premium-accent/5 to-card",
  },
];

export default function DecisionExample() {
  return (
    <section className="w-full max-w-3xl mx-auto py-6 sm:py-16 px-4 border-t border-border/15 overflow-hidden">
      <p className="text-[10px] text-muted-foreground/50 font-body uppercase tracking-wider text-center mb-2">Example decision</p>
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="text-xl sm:text-2xl font-display font-bold text-foreground text-center mb-5"
      >
        How Decyzon works
      </motion.h2>

      <motion.div
        initial={{ opacity: 0, y: 14 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="rounded-xl border border-border/60 bg-card p-4 sm:p-5 mb-4"
      >
        <p className="text-[10px] text-muted-foreground font-body uppercase tracking-wider mb-1">Query</p>
        <p className="text-sm font-body text-foreground font-medium">
          Best protein powder under $80 delivered tomorrow
        </p>
        <div className="border-t border-border/40 pt-3 mt-3">
          <div className="flex flex-col gap-1.5">
            {proofLines.map((line, i) => (
              <motion.div
                key={line}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 + i * 0.04 }}
                className="flex items-center gap-2"
              >
                <Check className="w-3 h-3 text-accent shrink-0" />
                <span className="text-xs font-body text-secondary-foreground">{line}</span>
              </motion.div>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground font-body mt-3">
            Returned 1 strongest decision and 2 useful alternatives.
          </p>
        </div>
      </motion.div>

      <div className="grid sm:grid-cols-3 gap-2.5">
        {results.map((r, i) => {
          const Icon = r.icon;
          return (
            <motion.div
              key={r.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: 0.12 + i * 0.06 }}
              className={`rounded-xl border border-border/50 bg-gradient-to-br ${r.gradient} p-3.5 flex flex-col gap-2 ${i === 0 ? "sm:ring-1 sm:ring-primary/10" : "opacity-90"}`}
            >
              <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium font-body w-fit ${r.badgeClass}`}>
                <Icon className="w-2.5 h-2.5" />
                {r.label}
              </div>
              <h3 className="text-sm font-display font-semibold text-foreground leading-tight">{r.name}</h3>
              <div className="flex gap-2.5 text-xs font-body">
                <span className="text-foreground font-semibold">{r.price}</span>
                <span className="text-muted-foreground">{r.delivery}</span>
              </div>
              <p className="text-[11px] text-secondary-foreground font-body leading-relaxed">{r.why}</p>
              <div className="flex items-start gap-1.5 px-2 py-1.5 rounded-md bg-muted/30 border border-border/30 mt-auto">
                <AlertTriangle className="w-2.5 h-2.5 text-score-mid shrink-0 mt-0.5" />
                <p className="text-[10px] text-muted-foreground font-body leading-relaxed">{r.tradeoff}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
      <p className="text-[9px] text-muted-foreground/40 font-body text-center mt-4">
        Illustrative example. Actual results vary by market and availability.
      </p>
    </section>
  );
}
